// HoldStatusConfiguration.tsx
import React from "react"
import { Box, Typography, Grid } from "@mui/material"
import { ErrorMessage, FormCard, FormDropdownFilter, FormDropdownFilterProps } from "components"
import { motion, Variants } from "framer-motion"
import * as hooks from "lib/hooks"
import * as containmentSubsequentHoldFilter from "enums/filters/containmentSubsequentHold"
import * as containmentInitialHoldFilter from "enums/filters/containmentInitialHold"
import { ContainmentHoldDTO } from "models/dto/containmentHold"
import { Control, FieldValues, Path } from "react-hook-form"
import { InputWrapperStyles, FormLabelStyle } from "lib/styles/shared"

export interface HoldStatusConfigurationProps<T extends FieldValues> {
	variants?: Variants
	control: Control<T>
	initialKey: Path<T>
	subsequentKey: Path<T>
	autoLoadIntialHold?: boolean
	autoLoadSubsequentHold?: boolean
	onInitialDataLoad?: (d: ContainmentHoldDTO[]) => void
	onSubsequentDataLoad?: (d: ContainmentHoldDTO[]) => void
	initialDisabled?: boolean	
	getOptionDisabled?: (option: ContainmentHoldDTO) => boolean
	onChange?: FormDropdownFilterProps<
		T,
		"containmentInitialHold" | "containmentSubsequentHold",
		ContainmentHoldDTO,
		ContainmentHoldDTO,
		ContainmentHoldDTO | null
	>["onChange"]
}

export function HoldStatusConfiguration<T extends FieldValues>({
	control,
	initialKey,
	subsequentKey,
	autoLoadIntialHold,
	autoLoadSubsequentHold,
	onInitialDataLoad,
	onSubsequentDataLoad,
	initialDisabled,
	getOptionDisabled,
	onChange,
}: HoldStatusConfigurationProps<T>) {
	const { theme } = hooks.UseTheme()

	// Memoize filter types to prevent recreating them on every render (causes duplicate API calls)
	const initialHoldFilterType = React.useMemo(
		() => containmentInitialHoldFilter.Dropdown(),
		[]
	)
	const subsequentHoldFilterType = React.useMemo(
		() => containmentSubsequentHoldFilter.Dropdown(),
		[]
	)

	return (
		<FormCard label="Hold Type" variant="nested" toolTip="Select types for initial and subsequent holds">
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						component={motion.div}
						initial={{ opacity: 0, y: 4 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25 }}
					>
						<Typography sx={FormLabelStyle}>On Initial Load</Typography>
						<Box
							data-test-selector="initial-hold-dropdown"
							sx={InputWrapperStyles(theme)}
						>
							<FormDropdownFilter
								control={control}
								name={initialKey}
								autoTrigger={autoLoadIntialHold}
								filterType={initialHoldFilterType}
								onDataLoaded={onInitialDataLoad}
								disabled={autoLoadIntialHold || initialDisabled}
								keyFunc={(a) => a.id}
								labelFunc={(a) => a.name}
								onChange={onChange}
								rules={{ required: "Initial hold status is required" }}
								getOptionDisabled={getOptionDisabled}
							/>
						</Box>
					</Box>
				</Grid>

				<Grid item xs={12}>
					<Box
						component={motion.div}
						initial={{ opacity: 0, y: 4 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, delay: 0.05 }}
					>
						<Typography sx={FormLabelStyle}>On Subsequent Scan</Typography>
						<Box
							data-test-selector="subsequent-hold-dropdown"
							sx={InputWrapperStyles(theme)}
						>
						<FormDropdownFilter
							control={control}
							name={subsequentKey}
							autoTrigger={autoLoadSubsequentHold}
							filterType={subsequentHoldFilterType}
							onDataLoaded={onSubsequentDataLoad}
							keyFunc={(a) => a.id}
							labelFunc={(a) => a.name}
							onChange={onChange}
							rules={{ required: "Subsequent hold status is required" }}
							renderError={(err) =>
								err.IfErr((msg) => <ErrorMessage msg={msg} />).UnwrapOrNil()
							}
							getOptionDisabled={getOptionDisabled}
						/>
						</Box>
					</Box>
				</Grid>
			</Grid>
		</FormCard>
	)
}
